import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ACTION_TYPES,
  getLogs,
  exportPdf,
  syncOfflineLogs,
  getOfflineQueueCount,
} from '../../../services/auditLogService';
import { formatLogDetails } from '../../../utils/logDetailsFormat';
import './AuditLogManager.css';

const DETAIL_ANIMATION_MS = 280;
const MENU_ANIMATION_MS = 200;

// Animated expand/collapse detail row: mounts before opening and unmounts
// after closing so the grid-rows transition runs in both directions.
function DetailRow({ open, children }) {
  const [rendered, setRendered] = useState(open);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => setIsOpen(true))
      );
      return () => cancelAnimationFrame(raf);
    }
    setIsOpen(false);
    const timer = setTimeout(() => setRendered(false), DETAIL_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [open]);

  if (!rendered) return null;

  return (
    <tr className={`row-detail audit-detail-anim${isOpen ? ' open' : ''}`}>
      <td colSpan={7}>
        <div className="audit-detail-grid">
          <div className="audit-detail-inner">{children}</div>
        </div>
      </td>
    </tr>
  );
}

// Same lifecycle for the action-filter dropdown menu (200ms).
function MenuAnim({ open, children }) {
  const [rendered, setRendered] = useState(open);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setRendered(true);
      const raf = requestAnimationFrame(() =>
        requestAnimationFrame(() => setIsOpen(true))
      );
      return () => cancelAnimationFrame(raf);
    }
    setIsOpen(false);
    const timer = setTimeout(() => setRendered(false), MENU_ANIMATION_MS);
    return () => clearTimeout(timer);
  }, [open]);

  if (!rendered) return null;

  return (
    <div className={`audit-menu-anim${isOpen ? ' open' : ''}`}>
      <div className="audit-menu-inner">{children}</div>
    </div>
  );
}

const AuditLogManager = () => {
  const { t, i18n } = useTranslation();

  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedActionTypes, setSelectedActionTypes] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Data & UI state
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offlineCount, setOfflineCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [exporting, setExporting] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);

  // Server-side status counts (null when the backend doesn't provide them)
  const [serverSuccessCount, setServerSuccessCount] = useState(null);
  const [serverErrorCount, setServerErrorCount] = useState(null);

  // Per-category log counts for the action dropdown (from the backend)
  const [categoryCounts, setCategoryCounts] = useState(null);

  // Action dropdown
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
  const actionDropdownRef = useRef(null);

  // Stats (fall back to current page when server counts are unavailable)
  const successCount = serverSuccessCount ?? logs.filter(l => l.status === 'success').length;
  const errorCount = serverErrorCount ?? logs.filter(l => l.status === 'error').length;

  // Debounce free-text search so typing doesn't fire a request per keystroke
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Auto-dismiss sync feedback after a few seconds
  useEffect(() => {
    if (!syncMessage) return;
    const timer = setTimeout(() => setSyncMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [syncMessage]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (actionDropdownRef.current && !actionDropdownRef.current.contains(e.target)) {
        setActionDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close dropdown on scroll, but allow scrolling inside the dropdown itself
  useEffect(() => {
    if (!actionDropdownOpen) return;
    const handleScroll = (e) => {
      if (actionDropdownRef.current && actionDropdownRef.current.contains(e.target)) return;
      setActionDropdownOpen(false);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [actionDropdownOpen]);

  // Load offline count on mount and after each fetch
  const refreshOfflineCount = useCallback(async () => {
    try {
      const count = await getOfflineQueueCount();
      setOfflineCount(count);
    } catch {
      setOfflineCount(0);
    }
  }, []);

  useEffect(() => {
    refreshOfflineCount();
  }, [refreshOfflineCount]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshOfflineCount();
    }, 5000);
    return () => clearInterval(interval);
  }, [refreshOfflineCount]);

  // Fetch logs
  const fetchLogs = useCallback(async (page = 1) => {
    setIsLoading(true);
    setError(null);
    try {
      const filters = {
        page,
        perPage,
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
        ...(selectedActionTypes.length ? { actionType: selectedActionTypes } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      };
      const result = await getLogs(filters);
      setLogs(result.logs || []);
      setTotal(result.total || 0);
      setCurrentPage(result.currentPage || 1);
      setLastPage(result.lastPage || 1);
      setServerSuccessCount(result.successCount ?? null);
      setServerErrorCount(result.errorCount ?? null);
      setCategoryCounts(result.categoryCounts ?? null);
    } catch (err) {
      setError(err?.message || 'Failed to load audit logs');
      setLogs([]);
      setServerSuccessCount(null);
      setServerErrorCount(null);
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo, selectedActionTypes, statusFilter, debouncedSearch, perPage]);

  useEffect(() => {
    setCurrentPage(1);
    fetchLogs(1);
  }, [fetchLogs]);

  const handlePageChange = (page) => {
    if (page < 1 || page > lastPage) return;
    setCurrentPage(page);
    fetchLogs(page);
  };

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedActionTypes([]);
    setStatusFilter('');
    setSearchQuery('');
    setPerPage(25);
    setCurrentPage(1);
  };

  const handleActionToggle = (key) => {
    setSelectedActionTypes(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportPdf({
        dateFrom,
        dateTo,
        actionType: selectedActionTypes,
        status: statusFilter,
        search: debouncedSearch,
      });
    } catch (err) {
      setError(err?.message || 'Failed to export audit logs');
      setTimeout(() => setError(null), 4000);
    } finally {
      setExporting(false);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    setSyncMessage(null);
    try {
      const result = await syncOfflineLogs();
      if (result.success) {
        setOfflineCount(result.remaining);
        setSyncMessage({
          type: 'success',
          text: t('auditLog.syncSuccess', 'Synced {{synced}} logs ({{remaining}} remaining)', {
            synced: result.synced ?? 0,
            remaining: result.remaining ?? 0,
          }),
        });
        fetchLogs(currentPage);
      } else {
        setSyncMessage({
          type: 'error',
          text: result.error || t('auditLog.syncFailed', 'Failed to sync offline logs.'),
        });
      }
    } catch (err) {
      setSyncMessage({
        type: 'error',
        text: err?.message || t('auditLog.syncFailed', 'Failed to sync offline logs.'),
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const hasFilters = dateFrom || dateTo || selectedActionTypes.length || statusFilter || searchQuery;

  const actionTypeList = Object.values(ACTION_TYPES);

  // Actions grouped by category for the dropdown (unknown categories appended at the end)
  const CATEGORY_ORDER = ['user', 'post', 'clinic', 'system', 'cms', 'data-entry', 'referral', 'pet'];
  const orderedCategories = [
    ...CATEGORY_ORDER,
    ...[...new Set(actionTypeList.map(a => a.category))].filter(c => !CATEGORY_ORDER.includes(c)),
  ];
  const groupedActionTypes = orderedCategories
    .map(category => ({ category, actions: actionTypeList.filter(a => a.category === category) }))
    .filter(g => g.actions.length > 0);

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    // Normalize fractional seconds to milliseconds (backend sends microseconds)
    const normalized = String(iso).replace(/(\.\d{3})\d+/, '$1');
    const d = new Date(normalized);
    if (Number.isNaN(d.getTime())) return String(iso);
    return d.toLocaleString(i18n.language === 'ar' ? 'ar-SA' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionBadgeClass = (category) => {
    switch (category) {
      case 'user': return 'badge-user';
      case 'post': return 'badge-post';
      case 'clinic': return 'badge-clinic';
      case 'system': return 'badge-system';
      case 'cms': return 'badge-cms';
      case 'pet': return 'badge-pet';
      case 'data-entry': return 'badge-data-entry';
      case 'referral': return 'badge-referral';
      default: return 'badge-default';
    }
  };

  const toggleRow = (id) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const startItem = (currentPage - 1) * perPage + 1;
  const endItem = Math.min(currentPage * perPage, total);

  return (
    <div className="audit-log-manager">
      {/* Sync button (only when there are unsynced local logs) */}
      {offlineCount > 0 && (
        <div className="audit-log-meta-bar">
          <div className="audit-log-sync">
            <button
              className="sync-btn"
              onClick={handleSync}
              disabled={isSyncing}
              title={t('auditLog.syncNow', 'Sync now')}
            >
              {isSyncing ? (
                <span className="loading-spinner-small"></span>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                </svg>
              )}
              {t('auditLog.syncNow', 'Sync now')}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="composer-alert alert-error">
          {error}
          <button className="alert-close" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Sync feedback */}
      {syncMessage && (
        <div className={`composer-alert alert-${syncMessage.type}`}>
          {syncMessage.text}
          <button className="alert-close" onClick={() => setSyncMessage(null)}>✕</button>
        </div>
      )}

      {/* Filters */}
      <div className="audit-log-filters">
        <div className="filter-group">
          <label>{t('auditLog.dateFrom', 'From')}</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>{t('auditLog.dateTo', 'To')}</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />
        </div>

        <div className="filter-group" ref={actionDropdownRef}>
          <label>{t('auditLog.actionType', 'Action')}</label>
          <button
            className={`filter-dropdown-trigger${actionDropdownOpen ? ' open' : ''}${selectedActionTypes.length > 0 ? ' has-selection' : ''}`}
            onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
          >
            {selectedActionTypes.length === 0
              ? t('auditLog.allActions', 'All actions')
              : selectedActionTypes.length === 1
                ? ACTION_TYPES[selectedActionTypes[0]]?.label || selectedActionTypes[0]
                : t('auditLog.selectedCount', '{{count}} selected', { count: selectedActionTypes.length })}
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          {actionDropdownOpen && (
            <MenuAnim open={actionDropdownOpen}>
              <div className="filter-dropdown-menu">
              {groupedActionTypes.map(group => (
                <div key={group.category} className="filter-dropdown-group">
                  <div className="filter-dropdown-group-label">
                    <span className="filter-dropdown-group-title">
                      {t(`auditLog.categories.${group.category}`, group.category)}
                      {categoryCounts?.[group.category] > 0 && (
                        <span className="filter-dropdown-group-count">{categoryCounts[group.category]}</span>
                      )}
                    </span>
                  </div>
                  {group.actions.map(action => (
                    <label key={action.key} className="filter-dropdown-item">
                      <input
                        type="checkbox"
                        checked={selectedActionTypes.includes(action.key)}
                        onChange={() => handleActionToggle(action.key)}
                      />
                      <span className={`category-dot ${getActionBadgeClass(action.category)}`} aria-hidden="true"></span>
                      <span className="filter-dropdown-label">{action.label}</span>
                    </label>
                  ))}
                </div>
              ))}
              </div>
            </MenuAnim>
          )}
        </div>

        <div className="filter-group">
          <label>{t('auditLog.status', 'Result')}</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">{t('auditLog.allResults', 'All results')}</option>
            <option value="success">{t('auditLog.success', 'Success')}</option>
            <option value="error">{t('auditLog.failed', 'Failed')}</option>
          </select>
        </div>

        <div className="filter-group filter-search">
          <label>{t('auditLog.search', 'Search')}</label>
          <div className="filter-search-input">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={t('auditLog.searchPlaceholder', 'Search by name, email, or ID...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button className="search-clear" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>
        </div>

        {hasFilters && (
          <button className="filter-clear-btn" onClick={handleClearFilters}>
            {t('auditLog.clearFilters', 'Clear')}
          </button>
        )}
      </div>

      {/* Stats */}
      <div className="audit-log-stats">
        <div className="stat-box">
          <span className="stat-value">{total}</span>
          <span className="stat-label">{t('auditLog.totalLogs', 'Total')}</span>
        </div>
        <div className="stat-box stat-success">
          <span className="stat-value">{successCount}</span>
          <span className="stat-label">{t('auditLog.successCount', 'Success')}</span>
        </div>
        <div className="stat-box stat-error">
          <span className="stat-value">{errorCount}</span>
          <span className="stat-label">{t('auditLog.errorCount', 'Failed')}</span>
        </div>
      </div>

      {/* Export button */}
      <div className="audit-log-export-bar">
        <button className="btn-export" onClick={handleExport} disabled={exporting}>
          {exporting ? (
            <span className="loading-spinner-small"></span>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          )}
          {t('auditLog.export', 'Export')}
        </button>
      </div>

      {/* Table */}
      <div className="audit-log-table-wrap">
        {isLoading ? (
          <div className="audit-log-loading">
            <div className="loading-spinner"></div>
            <p>{t('auditLog.loading', 'Loading audit logs...')}</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="audit-log-empty">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
            <p>{t('auditLog.noLogs', 'No audit logs found.')}</p>
            {hasFilters && (
              <button className="btn-link" onClick={handleClearFilters}>
                {t('auditLog.clearFilters', 'Clear filters')}
              </button>
            )}
          </div>
        ) : (
          <>
            <table className="audit-log-table">
              <thead>
                <tr>
                  <th>{t('auditLog.dateTime', 'Date & Time')}</th>
                  <th>{t('auditLog.action', 'Action')}</th>
                  <th>{t('auditLog.admin', 'Admin')}</th>
                  <th>{t('auditLog.target', 'Target')}</th>
                  <th>{t('auditLog.whatHappened', 'What Happened')}</th>
                  <th>{t('auditLog.result', 'Result')}</th>
                  <th>{t('auditLog.duration', 'Time Taken')}</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log, idx) => {
                  const rowKey = log.id || idx;
                  const isExpanded = expandedId === rowKey;
                  const hasDetails = log.details != null &&
                    (typeof log.details === 'string'
                      ? log.details.trim() !== ''
                      : Object.keys(log.details).length > 0);
                  return (
                    <React.Fragment key={rowKey}>
                      <tr
                        className={`row-expandable${log.status === 'error' ? ' row-error' : ''}${isExpanded ? ' row-expanded' : ''}`}
                        onClick={() => toggleRow(rowKey)}
                      >
                        <td className="cell-datetime">
                          <span className="expand-wrap">
                            <svg className={`expand-chevron${isExpanded ? ' open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="9 18 15 12 9 6"></polyline>
                            </svg>
                            {formatDateTime(log.timestamp)}
                          </span>
                        </td>
                        <td className="cell-action">
                          <span className={`action-badge ${getActionBadgeClass(log.category)}`}>
                            {log.actionLabel || log.actionType}
                          </span>
                        </td>
                        <td className="cell-admin">
                          <div className="admin-info">
                            <span className="admin-name">{log.adminName || '—'}</span>
                            {log.adminEmail && <span className="admin-email">{log.adminEmail}</span>}
                          </div>
                        </td>
                        <td className="cell-target">
                          {(() => {
                            // No target at all
                            if (!log.targetId && !log.targetName) return <span className="target-name">—</span>;
                            // Has a real name
                            if (log.targetName && log.targetName !== 'Unknown' && log.targetName !== 'a target') {
                              return (
                                <>
                                  <span className="target-name">{log.targetName}</span>
                                  {log.targetId && <span className="target-id">#{log.targetId}</span>}
                                </>
                              );
                            }
                            // Only has ID — show type + ID
                            const typeLabel = log.targetType && log.targetType !== 'unknown'
                              ? log.targetType.charAt(0).toUpperCase() + log.targetType.slice(1)
                              : 'Item';
                            return <span className="target-name">{typeLabel} #{log.targetId}</span>;
                          })()}
                        </td>
                        <td className="cell-summary">
                          <span className="summary-text" title={log.summary}>
                            {log.summary || '—'}
                          </span>
                          {log.errorMessage && (
                            <span className="error-detail" title={log.errorMessage}>
                              {log.errorMessage}
                            </span>
                          )}
                        </td>
                        <td className="cell-status">
                          {log.status === 'success' ? (
                            <span className="status-badge status-success">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                              {t('auditLog.success', 'Success')}
                            </span>
                          ) : (
                            <span className="status-badge status-error">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                              </svg>
                              {t('auditLog.failed', 'Failed')}
                            </span>
                          )}
                        </td>
                        <td className="cell-duration">
                          {log.durationMs ? `${log.durationMs}ms` : '—'}
                        </td>
                      </tr>
                      <DetailRow open={isExpanded}>
                        <div className="log-detail-panel">
                              <div className="detail-block">
                                <span className="detail-label">{t('auditLog.summary', 'Summary')}</span>
                                <p className="detail-text">{log.summary || '—'}</p>
                              </div>
                              {log.status === 'error' && log.errorMessage && (
                                <div className="detail-block">
                                  <span className="detail-label">{t('auditLog.errorMessage', 'Error Message')}</span>
                                  <p className="detail-text detail-error-text">{log.errorMessage}</p>
                                </div>
                              )}
                              {hasDetails && (
                                <div className="detail-block">
                                  <span className="detail-label">{t('auditLog.details', 'Details')}</span>
                                  {typeof log.details === 'string' ? (
                                    <p className="detail-text">{log.details}</p>
                                  ) : (
                                    <div className="detail-meta-grid">
                                      {formatLogDetails(log.details).map((row) => (
                                        <div className="detail-meta-item" key={row.label}>
                                          <span className="detail-label">{row.label}</span>
                                          <span className="detail-text">{row.value}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              <div className="detail-meta-grid">
                                <div className="detail-meta-item">
                                  <span className="detail-label">{t('auditLog.category', 'Category')}</span>
                                  <span>
                                    <span className={`action-badge ${getActionBadgeClass(log.category)}`}>
                                      {log.category || '—'}
                                    </span>
                                  </span>
                                </div>
                                <div className="detail-meta-item">
                                  <span className="detail-label">{t('auditLog.actionType', 'Action Type')}</span>
                                  <span className="detail-text">{log.actionType || '—'}</span>
                                </div>
                                <div className="detail-meta-item">
                                  <span className="detail-label">{t('auditLog.ipAddress', 'IP Address')}</span>
                                  <span className="detail-text">{log.ipAddress || '—'}</span>
                                </div>
                                <div className="detail-meta-item">
                                  <span className="detail-label">{t('auditLog.timestamp', 'Timestamp')}</span>
                                  <span className="detail-text">{log.timestamp ? formatDateTime(log.timestamp) : '—'}</span>
                                </div>
                              </div>
                        </div>
                      </DetailRow>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination */}
            <div className="audit-log-pagination-wrap">
              <div className="audit-log-perpage">
                <label>{t('auditLog.perPage', 'Per page')}</label>
                <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="audit-log-pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  {t('auditLog.prev', 'Previous')}
                </button>
                <span>
                  {t('auditLog.showing', 'Showing {{start}}–{{end}} of {{total}}', {
                    start: startItem,
                    end: endItem,
                    total,
                  })}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= lastPage}
                >
                  {t('auditLog.next', 'Next')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AuditLogManager;
